import { orderService } from './order.service';
import { analyticsService } from './analytics.service';
import { productService } from './product.service';

export interface DashboardStats {
  // Ventes
  totalSales: number;
  totalSalesPrevious: number;
  salesChange: number;
  
  // Commandes
  totalOrders: number;
  totalOrdersPrevious: number;
  ordersChange: number;
  
  // Commandes en attente
  pendingOrders: number;
  pendingOrdersPrevious: number;
  pendingChange: number;
  
  // Commandes annulées
  cancelledOrders: number;
  cancelledOrdersPrevious: number;
  cancelledChange: number;
  
  // Clients
  totalClients: number;
  totalProducts: number;
  productsInStock: number;
  productsOutOfStock: number;
  totalRevenue: number;
}

export interface TimeSeriesPoint {
  date: string;
  sales: number;
  orders: number;
}

export interface TopProduct {
  id: number;
  name: string;
  totalOrders: number;
  status: string;
  price: number;
}

export const dashboardService = {
  /**
   * Récupère les statistiques du dashboard
   */
  async getStats(): Promise<DashboardStats> {
    try {
      // Calculer les dates pour les 7 derniers jours et les 7 jours précédents
      const today = new Date();
      today.setHours(23, 59, 59, 999); // Fin de journée
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);
      sevenDaysAgo.setHours(0, 0, 0, 0); // Début de journée
      const fourteenDaysAgo = new Date(today);
      fourteenDaysAgo.setDate(today.getDate() - 14);
      fourteenDaysAgo.setHours(0, 0, 0, 0); // Début de journée
      
      const dateTo = today.toISOString().split('T')[0];
      const dateFrom = sevenDaysAgo.toISOString().split('T')[0];
      
      // Récupérer toutes les commandes
      let allOrders: any[] = [];
      try {
        allOrders = await orderService.getAll();
        console.log('📊 Dashboard - Total commandes récupérées:', allOrders.length);
      } catch (error) {
        console.error('Erreur lors de la récupération des commandes:', error);
        allOrders = [];
      }
      
      // Filtrer les commandes des 7 derniers jours
      const recentOrders = allOrders.filter(order => {
        if (!order.created_at) return false;
        try {
          const orderDate = new Date(order.created_at);
          // Vérifier que la date est valide
          if (isNaN(orderDate.getTime())) return false;
          return orderDate >= sevenDaysAgo && orderDate <= today;
        } catch (e) {
          console.warn('Erreur parsing date:', order.created_at, e);
          return false;
        }
      });
      
      // Filtrer les commandes des 7 jours précédents
      const previousOrders = allOrders.filter(order => {
        if (!order.created_at) return false;
        try {
          const orderDate = new Date(order.created_at);
          // Vérifier que la date est valide
          if (isNaN(orderDate.getTime())) return false;
          return orderDate >= fourteenDaysAgo && orderDate < sevenDaysAgo;
        } catch (e) {
          console.warn('Erreur parsing date:', order.created_at, e);
          return false;
        }
      });
      
      console.log('📊 Dashboard - Commandes récentes (7 derniers jours):', recentOrders.length);
      console.log('📊 Dashboard - Commandes précédentes (7 jours avant):', previousOrders.length);
      console.log('📊 Dashboard - Total commandes (toutes):', allOrders.length);
      
      // Calculer les ventes totales pour les 7 derniers jours
      // Inclure toutes les commandes (pas seulement payées) pour avoir le total réel
      const totalSales = recentOrders
        .reduce((sum, order) => {
          const total = Number(order.total) || 0;
          return sum + total;
        }, 0);
      
      const totalSalesPrevious = previousOrders
        .reduce((sum, order) => {
          const total = Number(order.total) || 0;
          return sum + total;
        }, 0);
      
      // Calculer les ventes totales globales (toutes les commandes)
      const totalSalesAll = allOrders
        .reduce((sum, order) => {
          const total = Number(order.total) || 0;
          return sum + total;
        }, 0);
      
      console.log('📊 Dashboard - Ventes totales (7 derniers jours):', totalSales);
      console.log('📊 Dashboard - Ventes totales (7 jours précédents):', totalSalesPrevious);
      console.log('📊 Dashboard - Ventes totales (toutes):', totalSalesAll);
      console.log('📊 Dashboard - Nombre de commandes avec total > 0:', allOrders.filter(o => parseFloat(o.total || 0) > 0).length);
      
      const salesChange = totalSalesPrevious > 0 
        ? ((totalSales - totalSalesPrevious) / totalSalesPrevious) * 100 
        : (totalSales > 0 ? 100 : 0);
      
      // Calculer les commandes totales
      const totalOrders = recentOrders.length;
      const totalOrdersPrevious = previousOrders.length;
      const ordersChange = totalOrdersPrevious > 0
        ? ((totalOrders - totalOrdersPrevious) / totalOrdersPrevious) * 100
        : (totalOrders > 0 ? 100 : 0);
      
      // Utiliser les statistiques globales depuis l'API (comme la page Commandes)
      let ordersStats: any = null;
      try {
        ordersStats = await orderService.getStats();
        console.log('📊 Dashboard - Statistiques globales:', ordersStats);
      } catch (error) {
        console.warn('Erreur lors de la récupération des statistiques globales:', error);
      }
      
      // Calculer les commandes en attente
      const pendingOrdersAll = ordersStats?.pending || allOrders.filter(o => o.order_status === 'pending').length;
      const pendingOrders = recentOrders.filter(o => o.order_status === 'pending').length;
      const pendingOrdersPrevious = previousOrders.filter(o => o.order_status === 'pending').length;
      const pendingChange = pendingOrdersPrevious > 0
        ? ((pendingOrders - pendingOrdersPrevious) / pendingOrdersPrevious) * 100
        : (pendingOrders > 0 ? 100 : 0);
      
      // Calculer les commandes annulées
      const cancelledOrdersAll = ordersStats?.cancelled || allOrders.filter(o => o.order_status === 'cancelled').length;
      const cancelledOrders = recentOrders.filter(o => o.order_status === 'cancelled').length;
      const cancelledOrdersPrevious = previousOrders.filter(o => o.order_status === 'cancelled').length;
      const cancelledChange = cancelledOrdersPrevious > 0
        ? ((cancelledOrders - cancelledOrdersPrevious) / cancelledOrdersPrevious) * 100
        : (cancelledOrders > 0 ? 100 : 0);
      
      // Utiliser le total global pour les commandes totales
      const totalOrdersGlobal = ordersStats?.total || allOrders.length;
      
      console.log('📊 Dashboard - Commandes en attente (toutes):', pendingOrdersAll);
      console.log('📊 Dashboard - Commandes annulées (toutes):', cancelledOrdersAll);
      console.log('📊 Dashboard - Total commandes (global):', totalOrdersGlobal);
      
      // Récupérer les KPIs analytics pour les clients
      let totalClients = 0;
      try {
        const kpis = await analyticsService.getKPIs(dateFrom, dateTo);
        totalClients = kpis.uniqueUsers || 0;
      } catch (error) {
        console.warn('Erreur lors de la récupération des KPIs:', error);
      }
      
      // Récupérer les produits
      let totalProducts = 0;
      let productsInStock = 0;
      let productsOutOfStock = 0;
      try {
        const products = await productService.getAll();
        totalProducts = products.length || 0;
        productsInStock = products.filter((p: any) => (p.stock || 0) > 0).length || 0;
        productsOutOfStock = products.filter((p: any) => (p.stock || 0) === 0).length || 0;
      } catch (error) {
        console.warn('Erreur lors de la récupération des produits:', error);
      }
      
      return {
        totalSales: totalSalesAll, // Utiliser toutes les ventes pour l'affichage
        totalSalesPrevious,
        salesChange,
        totalOrders: totalOrdersGlobal, // Utiliser le total global de toutes les commandes
        totalOrdersPrevious,
        ordersChange,
        pendingOrders: pendingOrdersAll, // Utiliser toutes les commandes en attente
        pendingOrdersPrevious,
        pendingChange,
        cancelledOrders: cancelledOrdersAll, // Utiliser toutes les commandes annulées
        cancelledOrdersPrevious,
        cancelledChange,
        totalClients,
        totalProducts,
        productsInStock,
        productsOutOfStock,
        totalRevenue: totalSalesAll, // Utiliser toutes les ventes
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      throw error;
    }
  },

  /**
   * Récupère les séries temporelles pour les graphiques
   * @param period 'this' pour cette semaine, 'last' pour la semaine dernière
   */
  async getTimeSeries(period: 'this' | 'last' = 'this'): Promise<TimeSeriesPoint[]> {
    try {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      
      let weekStart: Date;
      let weekEnd: Date;
      
      if (period === 'this') {
        // Cette semaine : 7 derniers jours (incluant aujourd'hui)
        weekEnd = new Date(today);
        weekStart = new Date(today);
        weekStart.setDate(today.getDate() - 6); // 7 jours incluant aujourd'hui = 6 jours en arrière
        weekStart.setHours(0, 0, 0, 0);
      } else {
        // La semaine dernière : 7 jours précédents (jours 8 à 14)
        weekEnd = new Date(today);
        weekEnd.setDate(today.getDate() - 7);
        weekEnd.setHours(23, 59, 59, 999);
        weekStart = new Date(weekEnd);
        weekStart.setDate(weekEnd.getDate() - 6); // 7 jours incluant le dernier jour
        weekStart.setHours(0, 0, 0, 0);
      }
      
      console.log(`📊 Dashboard - getTimeSeries(${period}):`, {
        weekStart: weekStart.toISOString(),
        weekEnd: weekEnd.toISOString()
      });
      
      // Récupérer les commandes pour calculer les ventes par jour
      const allOrders = await orderService.getAll();
      console.log(`📊 Dashboard - Total commandes pour getTimeSeries:`, allOrders.length);
      
      // Grouper les commandes par jour
      const salesByDay: Record<string, number> = {};
      const ordersByDay: Record<string, number> = {};
      
      allOrders.forEach(order => {
        if (!order.created_at) return;
        try {
          const orderDate = new Date(order.created_at);
          if (isNaN(orderDate.getTime())) return;
          
          // Vérifier si la commande est dans la période
          if (orderDate >= weekStart && orderDate <= weekEnd) {
            const dateKey = orderDate.toISOString().split('T')[0];
            
            if (!salesByDay[dateKey]) {
              salesByDay[dateKey] = 0;
              ordersByDay[dateKey] = 0;
            }
            
            // Inclure toutes les commandes (pas seulement payées) pour avoir le total réel
            const total = Number(order.total) || 0;
            salesByDay[dateKey] += total;
            ordersByDay[dateKey] += 1;
          }
        } catch (e) {
          console.warn('Erreur parsing date dans getTimeSeries:', order.created_at, e);
        }
      });
      
      // Créer les points de données pour les 7 jours de la période
      const result: TimeSeriesPoint[] = [];
      const baseDate = period === 'this' ? today : weekEnd;
      for (let i = 6; i >= 0; i--) {
        const date = new Date(baseDate);
        date.setDate(baseDate.getDate() - i);
        date.setHours(12, 0, 0, 0); // Mettre à midi pour éviter les problèmes de fuseau horaire
        const dateKey = date.toISOString().split('T')[0];
        
        const sales = salesByDay[dateKey] || 0;
        const orders = ordersByDay[dateKey] || 0;
        
        result.push({
          date: dateKey,
          sales: sales,
          orders: orders,
        });
        
        console.log(`📊 Dashboard - Point de données jour ${i}:`, {
          date: dateKey,
          sales: sales,
          orders: orders,
          salesByDayValue: salesByDay[dateKey]
        });
      }
      
      const totalSalesInResult = result.reduce((sum, p) => sum + p.sales, 0);
      console.log(`📊 Dashboard - TimeSeries (${period}) - Total ventes dans le résultat:`, totalSalesInResult);
      console.log(`📊 Dashboard - TimeSeries (${period}) complète:`, result);
      return result;
    } catch (error) {
      console.error('Erreur lors de la récupération des séries temporelles:', error);
      throw error;
    }
  },

  /**
   * Récupère les meilleurs produits
   */
  async getTopProducts(limit: number = 5): Promise<TopProduct[]> {
    try {
      const allOrders = await orderService.getAll();
      const products = await productService.getAll();
      
      // Compter les commandes par produit
      const productCounts: Record<number, number> = {};
      
      allOrders.forEach(order => {
        // Parser les items si c'est une chaîne JSON
        let items: any[] = [];
        if (typeof order.items === 'string') {
          try {
            items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
          } catch (e) {
            console.warn('Erreur parsing items:', e);
          }
        } else if (Array.isArray(order.items)) {
          items = order.items;
        }
        
        items.forEach((item: any) => {
          const productId = item.product_id || item.productId;
          if (productId) {
            productCounts[productId] = (productCounts[productId] || 0) + (item.quantity || 1);
          }
        });
      });
      
      // Créer la liste des produits avec leurs compteurs
      const productsWithCounts = products
        .map((product: any) => ({
          id: product.id,
          name: product.name,
          totalOrders: productCounts[product.id] || 0,
          status: (product.stock || 0) > 0 ? 'stock' : 'stock-out',
          price: product.price || 0,
        }))
        .filter((p: TopProduct) => p.totalOrders > 0)
        .sort((a: TopProduct, b: TopProduct) => b.totalOrders - a.totalOrders)
        .slice(0, limit);
      
      return productsWithCounts;
    } catch (error) {
      console.error('Erreur lors de la récupération des meilleurs produits:', error);
      throw error;
    }
  },

  /**
   * Récupère l'évolution des commandes par période (jour/semaine/mois)
   */
  async getOrdersEvolution(period: 'day' | 'week' | 'month'): Promise<TimeSeriesPoint[]> {
    try {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      
      let startDate: Date;
      let endDate: Date = new Date(today);
      const result: TimeSeriesPoint[] = [];
      
      // Récupérer toutes les commandes
      const allOrders = await orderService.getAll();
      console.log(`📊 Dashboard - getOrdersEvolution(${period}) - Total commandes:`, allOrders.length);
      
      if (period === 'day') {
        // 30 derniers jours
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 29); // 30 jours incluant aujourd'hui
        startDate.setHours(0, 0, 0, 0);
        
        // Grouper par jour
        const ordersByDay: Record<string, { sales: number; orders: number }> = {};
        
        allOrders.forEach(order => {
          if (!order.created_at) return;
          try {
            const orderDate = new Date(order.created_at);
            if (isNaN(orderDate.getTime())) return;
            
            const orderDateOnly = new Date(orderDate.getFullYear(), orderDate.getMonth(), orderDate.getDate());
            const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
            const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
            
            if (orderDateOnly >= startDateOnly && orderDateOnly <= endDateOnly) {
              const dateKey = orderDate.toISOString().split('T')[0];
              if (!ordersByDay[dateKey]) {
                ordersByDay[dateKey] = { sales: 0, orders: 0 };
              }
              ordersByDay[dateKey].sales += Number(order.total) || 0;
              ordersByDay[dateKey].orders += 1;
            }
          } catch (e) {
            console.warn('Erreur parsing date:', order.created_at, e);
          }
        });
        
        // Créer les points pour les 30 derniers jours
        for (let i = 29; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(today.getDate() - i);
          date.setHours(12, 0, 0, 0);
          const dateKey = date.toISOString().split('T')[0];
          
          result.push({
            date: dateKey,
            sales: ordersByDay[dateKey]?.sales || 0,
            orders: ordersByDay[dateKey]?.orders || 0,
          });
        }
      } else if (period === 'week') {
        // 12 dernières semaines
        startDate = new Date(today);
        startDate.setDate(today.getDate() - (12 * 7)); // 12 semaines
        startDate.setHours(0, 0, 0, 0);
        
        // Grouper par semaine
        const ordersByWeek: Record<string, { sales: number; orders: number }> = {};
        
        allOrders.forEach(order => {
          if (!order.created_at) return;
          try {
            const orderDate = new Date(order.created_at);
            if (isNaN(orderDate.getTime())) return;
            
            if (orderDate >= startDate && orderDate <= endDate) {
              // Calculer le numéro de semaine (année + semaine)
              const weekStart = new Date(orderDate);
              weekStart.setDate(orderDate.getDate() - orderDate.getDay()); // Dimanche de la semaine
              weekStart.setHours(0, 0, 0, 0);
              const weekKey = weekStart.toISOString().split('T')[0];
              
              if (!ordersByWeek[weekKey]) {
                ordersByWeek[weekKey] = { sales: 0, orders: 0 };
              }
              ordersByWeek[weekKey].sales += Number(order.total) || 0;
              ordersByWeek[weekKey].orders += 1;
            }
          } catch (e) {
            console.warn('Erreur parsing date:', order.created_at, e);
          }
        });
        
        // Créer les points pour les 12 dernières semaines
        for (let i = 11; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(today.getDate() - (i * 7));
          date.setDate(date.getDate() - date.getDay()); // Dimanche de la semaine
          date.setHours(12, 0, 0, 0);
          const weekKey = date.toISOString().split('T')[0];
          
          result.push({
            date: weekKey,
            sales: ordersByWeek[weekKey]?.sales || 0,
            orders: ordersByWeek[weekKey]?.orders || 0,
          });
        }
      } else if (period === 'month') {
        // 12 derniers mois
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 11); // 12 mois
        startDate.setDate(1); // Premier jour du mois
        startDate.setHours(0, 0, 0, 0);
        
        // Grouper par mois
        const ordersByMonth: Record<string, { sales: number; orders: number }> = {};
        
        allOrders.forEach(order => {
          if (!order.created_at) return;
          try {
            const orderDate = new Date(order.created_at);
            if (isNaN(orderDate.getTime())) return;
            
            if (orderDate >= startDate && orderDate <= endDate) {
              // Clé mois : YYYY-MM
              const monthKey = `${orderDate.getFullYear()}-${String(orderDate.getMonth() + 1).padStart(2, '0')}`;
              
              if (!ordersByMonth[monthKey]) {
                ordersByMonth[monthKey] = { sales: 0, orders: 0 };
              }
              ordersByMonth[monthKey].sales += Number(order.total) || 0;
              ordersByMonth[monthKey].orders += 1;
            }
          } catch (e) {
            console.warn('Erreur parsing date:', order.created_at, e);
          }
        });
        
        // Créer les points pour les 12 derniers mois
        for (let i = 11; i >= 0; i--) {
          const date = new Date(today);
          date.setMonth(today.getMonth() - i);
          date.setDate(1);
          date.setHours(12, 0, 0, 0);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          
          result.push({
            date: monthKey,
            sales: ordersByMonth[monthKey]?.sales || 0,
            orders: ordersByMonth[monthKey]?.orders || 0,
          });
        }
      }
      
      console.log(`📊 Dashboard - getOrdersEvolution(${period}) - Résultat:`, result);
      return result;
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'évolution des commandes:', error);
      throw error;
    }
  },
};
